import { useEffect, useMemo, useRef } from "react";
import { createPortal } from "react-dom";
import { giftById } from "./allinGifts";
import { playGiftSound } from "@/lib/celebrate";

const rnd = (n, seed) => Array.from({ length: n }, (_, i) => ({ i, x: (i * 37 + seed) % 100, d: ((i * 53) % 30) / 10, s: 14 + ((i * 29) % 26) }));

function KissArt() {
  return (
    <div className="relative w-56 h-56 flex items-center justify-center">
      <svg viewBox="0 0 100 92" className="w-48 h-48 gift-heartbeat drop-shadow-[0_0_35px_rgba(244,63,126,0.9)]" aria-hidden>
        <defs><linearGradient id="gh" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#ffd1e3" /><stop offset=".45" stopColor="#f43f7e" /><stop offset="1" stopColor="#b3164f" /></linearGradient></defs>
        <path d="M50 88 C20 66 2 50 2 28 C2 12 14 2 28 2 C38 2 46 8 50 16 C54 8 62 2 72 2 C86 2 98 12 98 28 C98 50 80 66 50 88Z" fill="url(#gh)" />
        <ellipse cx="30" cy="22" rx="10" ry="6" fill="#fff" opacity=".55" transform="rotate(-25 30 22)" />
      </svg>
      <span className="absolute text-7xl gift-kiss-fly" aria-hidden>💋</span>
      <span className="absolute -right-4 top-2 text-5xl gift-blow" aria-hidden>😘</span>
    </div>
  );
}

function LionArt() {
  return (
    <div className="relative w-56 h-56 flex items-center justify-center">
      <span className="absolute inset-6 rounded-full bg-amber-300/40 blur-2xl gift-glow" />
      <span className="relative text-[9rem] leading-none gift-lion drop-shadow-[0_0_30px_rgba(251,191,36,0.8)]" aria-hidden>🦁</span>
      <span className="absolute -top-2 -right-6 gift-bubble bg-white text-rose-600 font-display font-extrabold text-2xl px-4 py-2 rounded-3xl rounded-bl-md shadow-xl">Miau! 🐾</span>
    </div>
  );
}

function StarsArt() {
  return (
    <div className="relative w-56 h-56 flex items-center justify-center">
      <span className="absolute inset-4 rounded-full bg-amber-200/40 blur-2xl gift-glow" />
      <svg viewBox="0 0 100 100" className="relative w-44 h-44 gift-star-spin drop-shadow-[0_0_40px_rgba(253,224,71,0.95)]" aria-hidden>
        <defs><linearGradient id="gs" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stopColor="#fff7c2" /><stop offset=".5" stopColor="#fbbf24" /><stop offset="1" stopColor="#f472b6" /></linearGradient></defs>
        <path d="M50 4 L61 37 L96 37 L68 58 L78 92 L50 72 L22 92 L32 58 L4 37 L39 37Z" fill="url(#gs)" stroke="#fff" strokeWidth="2" strokeLinejoin="round" />
      </svg>
    </div>
  );
}

const ART = { kiss: KissArt, lion: LionArt, stars: StarsArt };
const RAIN = { kiss: ["💖", "💕", "💗", "💋"], lion: ["🐾", "✨", "🧡", "🐾"], stars: ["⭐", "✨", "🌟", "💫"] };

export function GiftAnimation({ gift, fromName, toName, onClose, note, action, hold = false }) {
  const g = giftById(gift);
  const Art = ART[g.id];
  const drops = useMemo(() => rnd(gift === "stars" ? 34 : 22, gift.length * 7), [gift]);
  const closeRef = useRef(onClose);
  closeRef.current = onClose;
  useEffect(() => { playGiftSound(gift); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (hold) return undefined; const t = setTimeout(() => closeRef.current(), 5000); return () => clearTimeout(t); }, [hold]);
  return createPortal(
    <div className="fixed inset-0 z-[96] flex items-center justify-center p-6 bg-[#1a0610]/80 backdrop-blur-md gift-fade" data-testid="allin-gift-animation" onClick={onClose}>
      <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden>
        <span className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[38rem] h-[38rem] rounded-full gift-rays" />
        {drops.map((d) => <span key={d.i} className={gift === "stars" ? "gift-fall" : "gift-rise"} style={{ left: `${d.x}%`, fontSize: d.s, animationDelay: `${d.d}s` }}>{RAIN[g.id][d.i % 4]}</span>)}
      </div>
      <div className="relative flex flex-col items-center text-center gift-pop" onClick={(e) => e.stopPropagation()}>
        <Art />
        <p className="mt-6 text-xs font-extrabold tracking-[0.35em] text-pink-200 uppercase">Geschenk</p>
        <h2 className="mt-2 font-display text-2xl sm:text-4xl font-extrabold text-white max-w-md leading-tight" data-testid="allin-gift-text">
          {fromName} hat {toName} <span className={`bg-gradient-to-r ${g.tone} bg-clip-text text-transparent`}>{g.art}</span> geschenkt
        </h2>
        {note && <p className="mt-3 text-sm font-semibold text-pink-100" data-testid="allin-gift-note">{note}</p>}
        <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
          {action}
          <button onClick={onClose} data-testid="allin-gift-close" className={`h-12 px-8 rounded-full font-extrabold active:scale-95 transition-transform ${action ? "bg-white/15 text-white ring-1 ring-white/40" : "pink-gradient text-white shadow-lg shadow-rose-500/40"}`}>{action ? "Schließen" : "Wie schön 💖"}</button>
        </div>
      </div>
    </div>,
    document.body
  );
}
